import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PipelineAdmin from "./admin/PipelineAdmin";
import EpisodesAdmin from "./admin/EpisodesAdmin";
import TagAlongsAdmin from "./admin/TagAlongsAdmin";
import SponsorsAdmin from "./admin/SponsorsAdmin";
import ContactsAdmin from "./admin/ContactsAdmin";
import SettingsAdmin from "./admin/SettingsAdmin";

const TABS = [
  { key: "pipeline", label: "Pipeline" },
  { key: "episodes", label: "Episodes" },
  { key: "tagalongs", label: "Tag-alongs" },
  { key: "sponsors", label: "Sponsors" },
  { key: "contacts", label: "Contacts" },
  { key: "settings", label: "Settings" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pipeline");
  const [apps, setApps] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [publishSource, setPublishSource] = useState(null);
  const [appsSort, setAppsSort] = useState("queue");

  const loadApps = () => api.get("/admin/applications", { params: { sort: appsSort } }).then(({ data }) => setApps(data)).catch(() => {});
  const loadEpisodes = () => api.get("/admin/episodes").then(({ data }) => setEpisodes(data)).catch(() => {});

  useEffect(() => { loadApps(); }, [appsSort]);
  useEffect(() => { loadEpisodes(); }, []);

  const updateApp = async (id, patch) => {
    try {
      const { data } = await api.patch(`/admin/applications/${id}`, patch);
      setApps((xs) => xs.map((a) => (a.id === id ? data : a)));
      if (selected?.id === id) setSelected(data);
      toast.success("Updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleLogout = async () => { await logout(); navigate("/admin/login"); };

  const onPublishAsEpisode = (app) => {
    setPublishSource(app);
    setSelected(null);
    setTab("episodes");
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-black sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2">
              <span className="w-2 h-2 bg-lime" />
              <span className="font-heading text-lg font-black tracking-tighter">PITCH.LU / ADMIN</span>
            </Link>
            <nav className="flex items-center gap-1">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  data-testid={`tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`px-4 py-2 text-xs font-mono uppercase tracking-widest ${tab === t.key ? "bg-lime text-black" : "text-white/60 hover:text-white"}`}
                >
                  {t.label}
                </button>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs font-mono text-white/50">{user?.email}</span>
            <button data-testid="logout-btn" onClick={handleLogout} className="px-3 py-1.5 border border-white/10 text-xs font-mono uppercase tracking-widest hover:border-lime hover:text-lime">Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-8">
        {tab === "pipeline" && (
          <PipelineAdmin
            apps={apps}
            appsSort={appsSort}
            setAppsSort={setAppsSort}
            onRefresh={loadApps}
            selected={selected}
            setSelected={setSelected}
            updateApp={updateApp}
            onPublishAsEpisode={onPublishAsEpisode}
          />
        )}
        {tab === "episodes" && (
          <EpisodesAdmin
            episodes={episodes}
            apps={apps}
            reload={loadEpisodes}
            publishSource={publishSource}
            setPublishSource={setPublishSource}
          />
        )}
        {tab === "tagalongs" && <TagAlongsAdmin episodes={episodes} />}
        {tab === "sponsors" && <SponsorsAdmin />}
        {tab === "contacts" && <ContactsAdmin />}
        {tab === "settings" && <SettingsAdmin />}
      </main>
    </div>
  );
}
